import { usePersistedState } from "@/lib/persistedFilters";
import { useEffect, useState, useCallback } from "react";
import { usePermissionPreview } from "@/components/permissions/PermissionPreview";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, MoreHorizontal, Pencil, Trash2, Search, Building2, Users as UsersIcon, Loader2, ShieldCheck, AppWindow, VenetianMask, Upload, Mail, LifeBuoy, LogOut } from "lucide-react";
import { SearchMatchHint } from "@/components/SearchMatchHint";
import { useUserSort, SortHeader, NameSortHint, LastNameSwitch, lastSignInLabel } from "@/components/UserSortControls";
import { UsersCsvButton } from "@/components/UsersCsvButton";
import { useOrgSort } from "@/components/OrgSortControls";
import { SignOutEverywhereDialog, toastAccessReset } from "@/components/SignOutEverywhere";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { ImportCustomersDialog } from "@/components/ImportCustomers";
import { StatusChip } from "@/components/Badges";
import { useHelpdeskLive } from "@/hooks/useHelpdeskLive";
import { EffectivePermsDialog } from "@/components/EffectivePerms";
import { AppAssignDialog } from "@/components/AppAssign";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const EMPTY = { name: "", type: "customer", parent_org_id: "none", status: "active", contact_name: "", contact_email: "", contact_phone: "", location: "", notes: "" };
const EMPTY_USER = { name: "", email: "", password: "", role: "customer" };

export default function Customers() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const helpdeskLive = useHelpdeskLive();
  const { has, user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const orgSort = useOrgSort(orgs);
  const [partners, setPartners] = useState([]);
  const [roles, setRoles] = useState([]);
  // Iter 110af — Seed `search` from `?q=` so the top-bar Global
  // Search can deep-link to a specific customer, and re-sync on
  // subsequent navigations (see effect below).
  const [search, setSearch] = usePersistedState("customers.search", "", searchParams.get("q"));
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  // Iter 108g — Open-ticket counts per customer org, powering the
  // small pill in the Helpdesk column. Empty for callers without
  // `helpdesk.view`.
  const [openCounts, setOpenCounts] = useState({});
  useEffect(() => {
    if (!has("helpdesk.view")) return;
    if (user?.role !== "super_admin" && user?.base_role !== "super_admin") return;
    api.get("/helpdesk/counts-by-org")
      .then((r) => setOpenCounts(r.data?.counts || {}))
      .catch(() => setOpenCounts({}));
  }, [has, user]);

  // users-under-customer
  const [usersTarget, setUsersTarget] = useState(null);
  const [custUsers, setCustUsers] = useState([]);
  const userSort = useUserSort(custUsers);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [userForm, setUserForm] = useState(EMPTY_USER);
  const [savingUser, setSavingUser] = useState(false);
  const [permsTarget, setPermsTarget] = useState(null);
  const [signOutTarget, setSignOutTarget] = useState(null);
  const [appsOrgTarget, setAppsOrgTarget] = useState(null);
  const [appsUserTarget, setAppsUserTarget] = useState(null);
  const [appCounts, setAppCounts] = useState({});

  const loadAppCounts = useCallback(() => {
    if (!has("apps.edit")) return;
    api.get("/apps").then((r) => {
      const m = {};
      r.data.forEach((a) => (a.allowed_orgs || []).forEach((oid) => { m[oid] = (m[oid] || 0) + 1; }));
      setAppCounts(m);
    }).catch(() => {});
  }, [has]);
  useEffect(() => { loadAppCounts(); }, [loadAppCounts]);

  const canMasquerade = (user?.base_role || user?.role) === "staff" && !user?.masqueraded_by;
  const startMasquerade = async (u) => {
    try {
      await api.post("/auth/masquerade/start", { target_user_id: u.user_id });
      window.location.href = "/dashboard";
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start masquerade"); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/organizations", { params: { type: "customer", ...(search ? { search } : {}) } });
      setOrgs(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  // Iter 110af — Re-sync `search` from URL `?q=` on subsequent
  // top-bar Global Search hits (avoids the stale-filter bug where
  // the URL updates but the input keeps the old query).
  useEffect(() => {
    const qp = searchParams.get("q");
    if (qp && qp !== search) setSearch(qp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  // A cleared search also drops a deep-link ?q, so a reload doesn't bring it back.
  useEffect(() => {
    if (search || !searchParams.get("q")) return;
    const next = new URLSearchParams(searchParams); next.delete("q");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
  useEffect(() => {
    api.get("/organizations", { params: { type: "partner" } }).then((r) => setPartners(r.data)).catch(() => {});
    api.get("/meta").then((r) => setRoles(r.data.roles)).catch(() => {});
  }, []);

  const customerRoles = roles.filter((r) => r.base_role === "customer");

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (o) => { setEditing(o); setForm({ ...EMPTY, ...o, type: "customer", parent_org_id: o.parent_org_id || "none" }); setDialogOpen(true); };

  const save = async () => {
    const payload = { ...form, type: "customer", partner_type: null, parent_org_id: form.parent_org_id === "none" ? null : form.parent_org_id };
    try {
      if (editing) { await api.put(`/organizations/${editing.org_id}`, payload); toast.success("Customer updated"); }
      else { await api.post("/organizations", payload); toast.success("Customer created"); }
      setDialogOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/organizations/${deleteTarget.org_id}`); toast.success("Customer deleted"); setDeleteTarget(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openUsers = async (o) => {
    setUsersTarget(o); setCustUsers([]); setAddingUser(false); setUserForm(EMPTY_USER); setLoadingUsers(true);
    try { const { data } = await api.get("/users", { params: { organization_id: o.org_id } }); setCustUsers(data); }
    catch { toast.error("Could not load users."); }
    finally { setLoadingUsers(false); }
  };
  const reloadUsers = async () => {
    if (!usersTarget) return;
    const { data } = await api.get("/users", { params: { organization_id: usersTarget.org_id } });
    setCustUsers(data);
  };
  const addUser = async () => {
    if (!userForm.name.trim() || !userForm.email.trim()) { toast.error("Name and email are required"); return; }
    setSavingUser(true);
    try {
      await api.post("/users", { ...userForm, organization_id: usersTarget.org_id });
      toast.success("User added");
      setUserForm(EMPTY_USER); setAddingUser(false);
      await reloadUsers(); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingUser(false); }
  };

  const [welcomingUserId, setWelcomingUserId] = useState(null);

  // Iter 268 — customer users get the same affordances partner users have:
  // edit, delete, and an active/inactive switch, so a customer contact can be
  // corrected or stood down without leaving this dialog.
  const canChangeRole = user?.role === "super_admin" || has("user_roles.edit");
  const [editingUser, setEditingUser] = useState(null);
  const preview = usePermissionPreview();
  const [editForm, setEditForm] = useState({ name: "", email: "", phone: "", role: "customer" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [statusBusyId, setStatusBusyId] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);

  const openEditUser = (u) => {
    setEditingUser(u);
    setEditForm({ name: u.name || "", email: u.email || "", phone: u.phone || "", role: u.role || "customer" });
  };

  const saveEditUser = async () => {
    if (!editForm.name.trim()) { toast.error("Name is required"); return; }
    const email = editForm.email.trim().toLowerCase();
    if (!email || !email.includes("@")) { toast.error("Enter a valid email address"); return; }
    setSavingEdit(true);
    if (canChangeRole && editForm.role !== editingUser.role && !(await preview.confirm(
      `/users/${editingUser.user_id}/permission-preview`, { role: editForm.role }, `Change ${editingUser.name || "this user"}'s role?`))) {
      setSavingEdit(false); return;
    }
    try {
      const { data } = await api.put(`/users/${editingUser.user_id}`, {
        name: editForm.name.trim(),
        email,
        phone: editForm.phone.trim(),
        ...(canChangeRole ? { role: editForm.role } : {}),
      });
      toast.success("User updated");
      toastAccessReset(data, editingUser.name);
      setEditingUser(null);
      await reloadUsers();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
    finally { setSavingEdit(false); }
  };

  const setUserStatus = async (u, active) => {
    setStatusBusyId(u.user_id);
    try {
      await api.post(`/users/${u.user_id}/status`, { status: active ? "active" : "deactivated" });
      toast.success(active ? `${u.name} activated` : `${u.name} deactivated`);
      await reloadUsers();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not change status"); }
    finally { setStatusBusyId(null); }
  };

  const confirmDeleteUser = async () => {
    const u = deletingUser;
    try {
      await api.delete(`/users/${u.user_id}`);
      toast.success(`${u.name} deleted`);
      setDeletingUser(null);
      await reloadUsers(); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed"); }
  };

  const [bulkWelcoming, setBulkWelcoming] = useState(false);
  const unwelcomedCount = custUsers.filter((u) => !u.welcome_sent_at).length;
  const sendWelcome = async (u) => {
    setWelcomingUserId(u.user_id);
    try {
      const { data } = await api.post(`/users/${u.user_id}/welcome`);
      toast.success(`Welcome email sent to ${data.email || u.email}`, { description: "Link expires in 24 hours." });
      reloadUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send welcome email");
    } finally {
      setWelcomingUserId(null);
    }
  };
  const bulkWelcome = async () => {
    if (!usersTarget?.org_id) return;
    setBulkWelcoming(true);
    try {
      const { data } = await api.post(`/organizations/${usersTarget.org_id}/welcome-unwelcomed`);
      const t = data.totals || {};
      toast.success(`Sent ${t.sent || 0} welcome email${t.sent === 1 ? "" : "s"}`, {
        description: [t.skipped ? `${t.skipped} skipped` : null, t.failed ? `${t.failed} failed` : null].filter(Boolean).join(" · ") || "All unwelcomed users invited.",
      });
      reloadUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk welcome failed");
    } finally { setBulkWelcoming(false); }
  };

  return (
    <div>
      <PageHeader testid="customers-header" title="Customers" subtitle="Customer accounts, their parent partner and assigned users.">
        {(has("customers.create") || user?.base_role === "partner") && (
          <Button variant="outline" data-testid="import-customers-button" onClick={() => setImportOpen(true)}>
            <Upload className="h-4 w-4" /> Import
          </Button>
        )}
        {has("customers.create") && (
          <Button data-testid="add-customer-button" onClick={openCreate} className="bg-primary hover:bg-primary/90 text-primary-foreground">
            <Plus className="h-4 w-4" /> Add customer
          </Button>
        )}
      </PageHeader>

      <ImportCustomersDialog open={importOpen} onOpenChange={setImportOpen} partners={partners}
        isPartner={user?.base_role === "partner"} onDone={load} />

      <div className="mb-4 flex justify-end">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="customers-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customers, contacts, users…" className="pl-9" />
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Name <span className="ml-1 text-[11px] font-normal"><NameSortHint sort={orgSort.sort} byLast={false} testid="customers-name-order" /></span></TableHead>
              <TableHead><SortHeader label="Parent Partner" sortKey="parent" sort={orgSort.sort} onToggle={orgSort.toggle} className="normal-case tracking-normal" testid="customers-sort-parent" /></TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Location</TableHead>
              <TableHead><SortHeader label="Users" sortKey="users" sort={orgSort.sort} onToggle={orgSort.toggle} className="normal-case tracking-normal" testid="customers-sort-users" /></TableHead>
              {has("helpdesk.view") && <TableHead>Helpdesk</TableHead>}
              <TableHead><SortHeader label="Status" sortKey="status" sort={orgSort.sort} onToggle={orgSort.toggle} className="normal-case tracking-normal" testid="customers-sort-status" /></TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={has("helpdesk.view") ? 8 : 7} className="py-10 text-center text-slate-400">Loading…</TableCell></TableRow>
            ) : orgs.length === 0 ? (
              <TableRow><TableCell colSpan={has("helpdesk.view") ? 8 : 7} className="py-10 text-center text-slate-400" data-testid="customers-empty">No customers found</TableCell></TableRow>
            ) : (
              orgSort.sorted.map((o) => (
                <TableRow key={o.org_id} data-testid={`customer-row-${o.org_id}`}>
                  <TableCell className="py-3">
                    <div className="flex items-center gap-3">
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-500"><Building2 className="h-4 w-4" /></span>
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-slate-800">{o.name}</span>
                        {has("apps.edit") && appCounts[o.org_id] > 0 && (
                          <button data-testid={`customer-app-count-${o.org_id}`} onClick={() => setAppsOrgTarget({ id: o.org_id, name: o.name })}
                            className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#eef2fb] px-1.5 py-0.5 align-middle text-[10px] font-medium text-primary hover:bg-[#e2e9f9]">
                            <AppWindow className="h-3 w-3" /> {appCounts[o.org_id]} app{appCounts[o.org_id] > 1 ? "s" : ""}
                          </button>
                        )}
                        <SearchMatchHint match={o.search_match} testid={`customer-search-match-${o.org_id}`} />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{o.parent_org_name || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-600">
                    {o.contact_name ? <div>{o.contact_name}<div className="text-xs text-slate-400">{o.contact_email}</div></div> : "—"}
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{o.location || "—"}</TableCell>
                  <TableCell>
                    <button data-testid={`customer-users-${o.org_id}`} onClick={() => openUsers(o)} className="inline-flex items-center gap-1.5 rounded-md bg-[#eef2fb] px-2 py-1 text-xs font-medium text-primary hover:bg-[#e2e9f9]">
                      <UsersIcon className="h-3.5 w-3.5" /> {o.user_count ?? 0}
                    </button>
                  </TableCell>
                  {has("helpdesk.view") && (() => {
                    // Iter 108g — Helpdesk load pill. Overdue turns red;
                    // clicking jumps to the Kanban filtered to this org.
                    const row     = openCounts[o.org_id] || {};
                    const cnt     = row.open_tickets || 0;
                    const overdue = row.overdue_tickets || 0;
                    return (
                      <TableCell>
                        <button data-testid={`customer-hd-count-${o.org_id}`}
                          onClick={() => navigate(`/helpdesk?org=${o.org_id}`)}
                          className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                            overdue > 0
                              ? "border border-red-300 bg-red-50 text-red-800 hover:bg-red-100"
                              : cnt > 0
                                ? "border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                : "border border-slate-200 bg-slate-50 text-slate-400 hover:bg-slate-100"
                          }`}
                          title={overdue > 0
                            ? `${cnt} open · ${overdue} overdue`
                            : cnt > 0
                              ? `${cnt} open ticket${cnt === 1 ? "" : "s"}`
                              : "No open tickets"}>
                          <LifeBuoy className="h-3.5 w-3.5" /> {cnt}
                          {overdue > 0 && (
                            <span className="rounded-full bg-red-600/90 px-1.5 text-[10px] font-semibold text-white">{overdue}</span>
                          )}
                        </button>
                      </TableCell>
                    );
                  })()}
                  <TableCell><StatusChip status={o.status} /></TableCell>
                  <TableCell>
                    {(has("customers.edit") || has("customers.delete")) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`customer-actions-${o.org_id}`} className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {has("customer_users.view") && <DropdownMenuItem onClick={() => openUsers(o)}><UsersIcon className="h-4 w-4" /> Manage users</DropdownMenuItem>}
                          {has("customers.edit") && <DropdownMenuItem onClick={() => openEdit(o)}><Pencil className="h-4 w-4" /> Edit</DropdownMenuItem>}
                          {has("helpdesk.view") && helpdeskLive && <DropdownMenuItem onClick={() => navigate(`/helpdesk?org=${o.org_id}`)} data-testid={`customer-tickets-${o.org_id}`}><LifeBuoy className="h-4 w-4" /> Helpdesk tickets</DropdownMenuItem>}
                          {has("apps.edit") && <DropdownMenuItem onClick={() => setAppsOrgTarget({ id: o.org_id, name: o.name })} data-testid={`customer-apps-${o.org_id}`}><AppWindow className="h-4 w-4" /> Manage apps</DropdownMenuItem>}
                          {has("customers.delete") && <DropdownMenuItem onClick={() => setDeleteTarget(o)} className="text-red-600 focus:text-red-600"><Trash2 className="h-4 w-4" /> Delete</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="flex max-h-[85vh] max-w-md flex-col gap-0 p-0" data-testid="customer-edit-dialog">
          <DialogHeader className="border-b border-border px-6 py-4">
            <DialogTitle>{editing ? "Edit customer" : "Add customer"}</DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-6 py-4" data-testid="customer-edit-scroll">
            <div className="space-y-1.5"><Label>Company name</Label>
              <Input data-testid="customer-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Parent partner</Label>
                <Select value={form.parent_org_id} onValueChange={(v) => setForm({ ...form, parent_org_id: v })}>
                  <SelectTrigger data-testid="customer-parent-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None (direct)</SelectItem>{partners.map((p) => <SelectItem key={p.org_id} value={p.org_id}>{p.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="customer-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="suspended">Suspended</SelectItem><SelectItem value="deactivated">Deactivated</SelectItem></SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Contact name</Label><Input data-testid="customer-contact-name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Contact phone</Label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Contact email</Label><Input data-testid="customer-contact-email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Location</Label><Input data-testid="customer-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="City, Region" /></div>
            </div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter className="border-t border-border px-6 py-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="save-customer-button" onClick={save} className="bg-primary hover:bg-primary/90 text-primary-foreground">{editing ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!usersTarget} onOpenChange={(o) => !o && setUsersTarget(null)}>
        <DialogContent className="flex max-h-[80vh] max-w-[95vw] flex-col gap-0 p-0 sm:max-w-5xl" data-testid="customer-users-dialog">
          <DialogHeader className="shrink-0 border-b border-border px-6 py-4">
            <DialogTitle className="flex items-center gap-2"><UsersIcon className="h-5 w-5 text-primary" /> Users — {usersTarget?.name}</DialogTitle>
            <DialogDescription className="flex items-center justify-between gap-3">
              <span>People who belong to this customer account.</span>
              <span className="flex items-center gap-3">
                <LastNameSwitch checked={userSort.byLast} onChange={userSort.setByLast} testid="customer-users-lastname-switch" />
                <UsersCsvButton rows={userSort.sorted} name={usersTarget?.name} testid="customer-users-csv-download"
                  columns={["name", "email", "phone", "organization", "last_login", "welcome", "role", "status"]} />
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-6 py-4" data-testid="customer-users-scroll">
            {has("customer_users.edit") && !loadingUsers && unwelcomedCount > 0 && (
              <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2 text-xs" data-testid="customer-bulk-welcome-bar">
                <span className="text-emerald-800">
                  <b>{unwelcomedCount}</b> user{unwelcomedCount === 1 ? "" : "s"} in this customer {unwelcomedCount === 1 ? "hasn" : "haven"}&apos;t received a welcome yet.
                </span>
                <Button size="sm" variant="outline" data-testid="customer-bulk-welcome-btn"
                  onClick={bulkWelcome} disabled={bulkWelcoming}
                  className="h-7 border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-100">
                  {bulkWelcoming ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                  Welcome all {unwelcomedCount}
                </Button>
              </div>
            )}
            {loadingUsers ? (
              <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>
            ) : (
              <div className="divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="customer-users-list">
                {custUsers.length > 0 && (
                  <div className="hidden items-center gap-2 bg-slate-50/80 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400 sm:flex">
                    <div className="min-w-0 flex-1">User · <NameSortHint sort={userSort.sort} byLast={userSort.byLast} testid="customer-users-name-order" /></div>
                    <div className="w-20 shrink-0 text-right"><SortHeader label="Last sign-in" sortKey="last_login" sort={userSort.sort} onToggle={userSort.toggle} testid="customer-users-sort-last-login" /></div>
                    {has("customer_users.edit") && <div className="w-24 shrink-0 text-center">Invite</div>}
                    <div className="w-14 shrink-0 text-center">Access</div>
                    {canMasquerade && <div className="w-28 shrink-0 text-center">Sign in as</div>}
                    <div className="w-24 shrink-0 text-center"><SortHeader label="Role" sortKey="role" sort={userSort.sort} onToggle={userSort.toggle} testid="customer-users-sort-role" /></div>
                    <div className="w-24 shrink-0 text-center"><SortHeader label="Active" sortKey="status" sort={userSort.sort} onToggle={userSort.toggle} testid="customer-users-sort-status" /></div>
                    <div className="w-8 shrink-0" />
                  </div>
                )}
                {custUsers.length === 0 ? (
                  <div className="py-6 text-center text-sm text-slate-400">No users yet.</div>
                ) : userSort.sorted.map((u) => (
                  <div key={u.user_id} className="flex flex-wrap items-center gap-2 px-3 py-2.5 sm:flex-nowrap" data-testid={`customer-user-${u.user_id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-slate-800">{u.name}</div>
                      <div className="truncate text-xs text-slate-400" title={u.email}>{u.email}</div>
                    </div>
                    <div className={`w-20 shrink-0 text-right text-xs font-medium tabular-nums ${u.last_login ? "text-slate-600" : "text-slate-400"}`}
                         title={u.last_login ? `Last sign-in: ${new Date(u.last_login).toLocaleString()}` : "Never signed in"}
                         data-testid={`customer-user-lastlogin-${u.user_id}`}>
                      {lastSignInLabel(u.last_login)}
                    </div>
                    {has("customer_users.edit") && (
                      <div className="w-24 shrink-0 text-center">
                        <button data-testid={`customer-user-welcome-${u.user_id}`}
                          onClick={() => sendWelcome(u)} disabled={welcomingUserId === u.user_id}
                          title={u.welcome_sent_at ? `Resend welcome (last sent ${new Date(u.welcome_sent_at).toLocaleString()})` : "Send welcome email"}
                          className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-100 disabled:opacity-60">
                          {welcomingUserId === u.user_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                          {u.welcome_sent_at ? "Resend" : "Welcome"}
                        </button>
                      </div>
                    )}
                    <div className="flex w-14 shrink-0 items-center justify-center gap-1.5">
                      <button data-testid={`customer-user-perms-${u.user_id}`} onClick={() => setPermsTarget(u)} title="View effective permissions" className="text-slate-400 hover:text-primary"><ShieldCheck className="h-4 w-4" /></button>
                      {has("apps.edit") && <button data-testid={`customer-user-apps-${u.user_id}`} onClick={() => setAppsUserTarget({ id: u.user_id, name: u.name })} title="Manage app access" className="text-slate-400 hover:text-primary"><AppWindow className="h-4 w-4" /></button>}
                    </div>
                    {canMasquerade && (
                      <div className="w-28 shrink-0 text-center">
                        <button data-testid={`customer-user-masquerade-${u.user_id}`} onClick={() => startMasquerade(u)} title={`Masquerade as ${u.name}`} className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-100"><VenetianMask className="h-3.5 w-3.5" /> Masquerade</button>
                      </div>
                    )}
                    <div className="w-24 shrink-0 truncate text-center" title={u.role_label || u.role}>
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">{u.role_label || u.role}</span>
                    </div>
                    <div className="flex w-24 shrink-0 items-center justify-center gap-1.5">
                      {has("customer_users.edit") ? (
                        <>
                          <Switch checked={u.status === "active"} disabled={statusBusyId === u.user_id}
                            onCheckedChange={(v) => setUserStatus(u, v)}
                            title={u.status === "active" ? "Deactivate this user" : "Activate this user"}
                            data-testid={`customer-user-active-${u.user_id}`} />
                          {statusBusyId === u.user_id
                            ? <Loader2 className="h-3 w-3 animate-spin text-slate-400" />
                            : <span className={`text-[10px] font-medium ${u.status === "active" ? "text-emerald-600" : "text-slate-400"}`}>
                                {u.status === "active" ? "On" : "Off"}
                              </span>}
                        </>
                      ) : <StatusChip status={u.status} />}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0"
                          data-testid={`customer-user-actions-${u.user_id}`}><MoreHorizontal className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {has("customer_users.edit") && (
                          <DropdownMenuItem data-testid={`customer-user-edit-${u.user_id}`} onClick={() => openEditUser(u)}>
                            <Pencil className="h-4 w-4" /> Edit user
                          </DropdownMenuItem>
                        )}
                        {has("customer_users.edit") && (
                          <DropdownMenuItem data-testid={`customer-user-signout-${u.user_id}`} onClick={() => setSignOutTarget(u)}><LogOut className="h-4 w-4" /> Sign out everywhere</DropdownMenuItem>
                        )}
                        {has("customer_users.delete") && (
                          <DropdownMenuItem data-testid={`customer-user-delete-${u.user_id}`} onClick={() => setDeletingUser(u)}
                            className="text-rose-700 focus:text-rose-800">
                            <Trash2 className="h-4 w-4" /> Delete user
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ))}
              </div>
            )}

            {has("customer_users.create") && (addingUser ? (
              <div className="space-y-2 rounded-lg border border-slate-200 p-3" data-testid="customer-add-user-form">
                <div className="grid grid-cols-2 gap-2">
                  <Input data-testid="cust-user-name" placeholder="Full name" value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} />
                  <Input data-testid="cust-user-email" type="email" placeholder="Email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input data-testid="cust-user-password" type="password" placeholder="Temp password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} />
                  <Select value={userForm.role} onValueChange={(v) => setUserForm({ ...userForm, role: v })}>
                    <SelectTrigger data-testid="cust-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="customer">Customer</SelectItem>
                      {customerRoles.filter((r) => r.role_id !== "customer").map((r) => <SelectItem key={r.role_id} value={r.role_id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setAddingUser(false)}>Cancel</Button>
                  <Button size="sm" data-testid="cust-user-save" onClick={addUser} disabled={savingUser} className="bg-primary hover:bg-primary/90 text-primary-foreground">{savingUser && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Add user</Button>
                </div>
              </div>
            ) : (
              <Button variant="outline" data-testid="customer-add-user-button" onClick={() => setAddingUser(true)} className="w-full"><Plus className="h-4 w-4" /> Add user to this customer</Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingUser} onOpenChange={(o) => !o && setEditingUser(null)}>
        <DialogContent className="max-w-md" data-testid="customer-user-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit customer user</DialogTitle>
            <DialogDescription>Update this person's details, including their sign-in email.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Name</Label>
              <Input data-testid="customer-user-edit-name" value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Email</Label>
              <Input data-testid="customer-user-edit-email" type="email" value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
              <p className="text-[11px] text-slate-400">This is their sign-in identity — they'll log in with the new address right away.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Phone (optional)</Label>
              <Input data-testid="customer-user-edit-phone" value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Role</Label>
              {canChangeRole ? (
                <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                  <SelectTrigger data-testid="customer-user-edit-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">Customer</SelectItem>
                    {customerRoles.filter((r) => r.role_id !== "customer").map((r) => (
                      <SelectItem key={r.role_id} value={r.role_id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700"
                  data-testid="customer-user-edit-role-readonly">
                  {(customerRoles.find((r) => r.role_id === editForm.role) || {}).name || editForm.role || "—"}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)}>Cancel</Button>
            <Button onClick={saveEditUser} disabled={savingEdit} data-testid="customer-user-edit-save"
              className="bg-primary text-primary-foreground hover:bg-primary/90">
              {savingEdit && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {preview.dialog}

      <AlertDialog open={!!deletingUser} onOpenChange={(o) => !o && setDeletingUser(null)}>
        <AlertDialogContent data-testid="customer-user-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deletingUser?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingUser?.email} loses access immediately and this can&apos;t be undone. If you only want to
              pause them, close this and use the Active switch instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteUser} data-testid="customer-user-delete-confirm"
              className="bg-rose-600 text-white hover:bg-rose-700">Delete user</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <EffectivePermsDialog userId={permsTarget?.user_id} userName={permsTarget?.name} open={!!permsTarget} onClose={() => setPermsTarget(null)} />
      <SignOutEverywhereDialog user={signOutTarget} onClose={() => setSignOutTarget(null)} />
      <AppAssignDialog scope="org" target={appsOrgTarget} open={!!appsOrgTarget} onClose={() => { setAppsOrgTarget(null); loadAppCounts(); }} />
      <AppAssignDialog scope="user" target={appsUserTarget} open={!!appsUserTarget} onClose={() => setAppsUserTarget(null)} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete customer?</AlertDialogTitle>
            <AlertDialogDescription>This removes {deleteTarget?.name}. This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-customer" onClick={doDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
